import { computed, inject, Injectable, resource, Signal } from '@angular/core';
import { TenantService } from '@cmi/addin-core';
import { Dossier, SearchDossierResponse } from './dossier.models';
import { DossierService } from './dossier.service';

/** The shortest term searched for; a shorter one searches nothing. */
export const MIN_SEARCH_LENGTH = 3;

/** What a dossier search found, and whether it is loading or failed. */
export interface DossierSearchResults {
  /** Undefined while nothing was searched for, or the search is loading or failed. */
  dossiers: Signal<Dossier[] | undefined>;
  loading: Signal<boolean>;
  failed: Signal<boolean>;
  reload(): void;
}

/** Dossier searches as the user types. */
@Injectable({
  providedIn: 'root'
})
export class DossierSearchService {
  private readonly _dossierService = inject(DossierService);
  private readonly _tenantService = inject(TenantService);

  /**
   * Searches whenever `term` changes, and again when the tenant changes.
   *
   * Call it in the caller's injection context: the search ends with the caller.
   */
  public search(term: () => string): DossierSearchResults {
    const results = resource<
      SearchDossierResponse | undefined,
      { searchTerm: string; tenant: string | null }
    >({
      params: () => ({
        searchTerm: term(),
        tenant: this._tenantService.selectedTenantName()
      }),
      loader: ({ params }) =>
        params.searchTerm.length < MIN_SEARCH_LENGTH
          ? Promise.resolve(undefined)
          : this._dossierService.searchDossier(params.searchTerm)
    });

    return {
      dossiers: computed(() => (results.hasValue() ? results.value().results : undefined)),
      loading: computed(() => results.isLoading()),
      failed: computed(() => results.error() !== undefined),
      reload: (): void => void results.reload()
    };
  }
}
